/**
 * PublicEventCard — canonical event card for ALL public views.
 * Mobile-first. Desktop = mobile × 1.1 (layout scale only).
 *
 * Media: media_assets pipeline only (never legacy fields).
 * Timing: timing_mode is source of truth.
 * Price: never "0 kr". CTA: "Se aktivitet →".
 */

import { Clock, MapPin, ArrowRight, Calendar, Wine, ShoppingBasket } from "lucide-react";
import { cn } from "@/lib/utils";
import { EventTypeBadge } from "@/components/events/EventTypeBadge";
import type { EventType } from "@/types/events";
import { Skeleton } from "@/components/ui/skeleton";
import { useCoverImageUrl } from "@/hooks/useMediaAssets";
import {
  formatTimingBadge,
  formatTimeRow,
  formatSeasonText,
  formatPlaceLabel,
  type TimingMode,
} from "@/lib/events/eventCardUtils";

export interface PublicEventCardEvent {
  id: string;
  title: string;
  startDate: string;
  endDate?: string | null;
  timingMode: TimingMode;
  outingTags?: string[];
  producerName: string | null;
  producerCity?: string | null;
  /** Custom location label (custom_address or place name from RPC) */
  location?: string | null;
  /** Pre-resolved cover URL from batch covers. If absent, hook fetches. */
  coverUrl?: string | null;
  /** Event type for badge display */
  eventType?: EventType;
  /** Event-level farm sale signals */
  includesFarmSale?: boolean;
  includesFarmSaleAlcohol?: boolean;
  /** Number of occurrences (for courses) */
  occurrencesCount?: number;
}

interface PublicEventCardProps {
  event: PublicEventCardEvent;
  /** Pre-resolved cover image URL (skip media hook if provided) */
  coverImageUrl?: string | null;
  /** Fallback cover URL when the activity has no image (e.g. producer cover) */
  fallbackCoverUrl?: string | null;
  onClick?: () => void;
  variant?: "list" | "map";
  /** Show EventTypeBadge pill (default true). Set false when context already implies type. */
  showTypeBadge?: boolean;
  isSelected?: boolean;
  isHighlighted?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  className?: string;
}

export function PublicEventCard({
  event,
  coverImageUrl: coverProp,
  fallbackCoverUrl,
  onClick,
  variant = "list",
  showTypeBadge = true,
  isSelected,
  isHighlighted,
  onMouseEnter,
  onMouseLeave,
  className,
}: PublicEventCardProps) {
  // Use prop cover first, then event.coverUrl, then hook fallback, then producer fallback
  const hookCover = useCoverImageUrl("event", !coverProp && !event.coverUrl ? event.id : null, 640);
  const coverUrl = coverProp ?? event.coverUrl ?? hookCover ?? fallbackCoverUrl ?? null;

  const badge = formatTimingBadge(event.timingMode, event.startDate, event.outingTags, event.endDate);
  const timeRow = formatTimeRow(event.timingMode, event.startDate, event.endDate);
  const placeLabel = formatPlaceLabel(event.producerName, event.producerCity, event.location);

  return (
    <article
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={cn(
        "relative bg-card rounded-xl overflow-hidden transition-all duration-200 cursor-pointer group border",
        isSelected
          ? "border-primary/50 bg-primary/5 shadow-md ring-1 ring-primary/20"
          : isHighlighted
            ? "border-primary/30 bg-primary/5 shadow-sm"
            : "border-transparent hover:border-border/50 hover:shadow-md",
        className,
      )}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onClick?.()}
      data-event-id={event.id}
    >
      {/* Cover image 3:2 */}
      <div className="aspect-[3/2] w-full overflow-hidden bg-muted relative">
        {coverUrl ? (
          <img
            src={coverUrl}
            alt={event.title}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-muted to-muted/50">
            <Calendar className="h-12 w-12 text-muted-foreground/30" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-3 sm:p-4 space-y-1.5">
        {/* Date label — primary scanning anchor */}
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-primary">
            {badge}
          </span>
          {showTypeBadge && <EventTypeBadge type={event.eventType || 'event'} />}
        </div>

        {/* Title */}
        <h3 className="font-display text-base sm:text-lg font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2">
          {event.title}
        </h3>

        {/* Place & time — stacked */}
        <div className="flex flex-col gap-0.5">
          {placeLabel && (
            <div className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
              <MapPin aria-hidden className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{placeLabel}</span>
            </div>
          )}
          {timeRow && (
            <div className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
              <Clock aria-hidden className="h-3.5 w-3.5 shrink-0" />
              <span>{timeRow}</span>
            </div>
          )}
        </div>

        {/* Course occurrence count */}
        {event.eventType === 'course' && (event.occurrencesCount ?? 0) > 0 && (
          <p className="text-xs text-muted-foreground">
            {event.occurrencesCount} tillfällen
          </p>
        )}

        {/* Sale signals */}
        {(event.includesFarmSale || event.includesFarmSaleAlcohol) && (
          <div className="flex items-center gap-2 flex-wrap">
            {event.includesFarmSaleAlcohol && (
              <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                <Wine aria-hidden className="h-3.5 w-3.5 shrink-0" />
                Dryck
              </span>
            )}
            {event.includesFarmSale && !event.includesFarmSaleAlcohol && (
              <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                <ShoppingBasket aria-hidden className="h-3.5 w-3.5 shrink-0" />
                Handla från gården
              </span>
            )}
          </div>
        )}

        {/* CTA */}
        <div className="pt-2 flex items-center">
          <span className="inline-flex items-center gap-1 text-sm font-medium text-primary group-hover:gap-2 transition-all">
            Se aktivitet
            <ArrowRight className="h-3.5 w-3.5" />
          </span>
        </div>
      </div>
    </article>
  );
}
